import { useEffect, useState } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { orchestrationService } from '@/services/orchestrationService';
import { logger } from '@/utils/logger';

/**
 * Master Content Bootstrap
 * Automatically triggers and monitors the content orchestration process
 * Runs silently in the background for admin/staff users
 */
export const MasterContentBootstrap = () => {
  const { user } = useSupabaseAuth();
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    const initializeOrchestration = async () => {
      if (!user || hasTriggered) return;

      // Get user role from metadata
      const userRole = user.user_metadata?.role || 'user';
      
      // Only run for admin/staff
      if (!['admin', 'staff'].includes(userRole)) return;

      logger.info('🎯 Master Content Orchestrator - Admin/Staff detected');

      try {
        // Check current state
        const state = await orchestrationService.getState();
        
        // If already complete, skip
        if (state?.auto_generation_complete && state?.completed_at) {
          logger.info('✅ Content orchestration already complete');
          setHasTriggered(true);
          return;
        }

        // Trigger orchestrator
        logger.info('🚀 Triggering master content orchestrator...');
        const result = await orchestrationService.triggerOrchestrator();
        
        if (result.success) {
          logger.info('✅ Orchestrator triggered successfully');
          setHasTriggered(true);
          
          // Start monitoring progress (every 60 seconds)
          startProgressMonitoring();
        } else {
          logger.error('❌ Failed to trigger orchestrator:', result.message);
        }
      } catch (error) {
        logger.error('Error initializing orchestration:', error);
      }
    };

    initializeOrchestration();
  }, [user, hasTriggered]);

  // Monitor progress until complete
  const startProgressMonitoring = () => {
    const interval = setInterval(async () => {
      const isComplete = await orchestrationService.isComplete();
      
      if (isComplete) {
        logger.info('🎉 Content orchestration COMPLETE!');
        clearInterval(interval);
      } else {
        const progress = await orchestrationService.getProgress();
        logger.info(`📊 Orchestration progress: ${progress}%`);
      }
    }, 60000); // Check every 60 seconds

    // Cleanup on unmount
    return () => clearInterval(interval);
  };

  return null; // This is a logic-only component
};
